/**
 * DFSPH (Divergence-Free SPH) Solver Implementation
 *
 * Implements the Divergence-Free Smoothed Particle Hydrodynamics method
 * with predictive-corrective pressure projection scheme.
 *
 * Key Features:
 * - Density invariance through alpha factors
 * - Divergence-free velocity field
 * - Constant density enforcement
 * - Iterative pressure projection
 *
 * Pipeline Stages:
 * 1. Compute density and alpha factors
 * 2. Apply non-pressure forces (viscosity, surface tension)
 * 3. Predict velocity
 * 4. Divergence-free correction (2 iterations default)
 * 5. Constant density correction (3 iterations default)
 *
 * @see ISPHSolver
 */

import type { ISPHSolver, GPUSolverBuffers, BindGroupGirisi } from './ISPHSolver';
import type { DFSPHParametreler } from '../FikoSPHParametreler';
import {
  DFSPH_COMPUTE_DENSITY_ALPHA,
  DFSPH_NON_PRESSURE_FORCES,
  DFSPH_PREDICT_VELOCITY,
  DFSPH_DIVERGENCE_SOLVE,
  DFSPH_DENSITY_SOLVE,
  DFSPH_SOLVER_BINDINGS
} from './sph-dfsph-shaders.wgsl';

/**
 * FikoDFSPHSolver - GPU-accelerated DFSPH implementation
 *
 * Uses 5 compute pipelines for the DFSPH algorithm:
 * - Density/Alpha computation
 * - Non-pressure forces
 * - Velocity prediction
 * - Divergence correction
 * - Density correction
 */
export class FikoDFSPHSolver implements ISPHSolver {
  readonly ad = 'DFSPH';
  readonly passSayisi = 8; // 1 density+alpha, 1 nonPressure, 1 predict, 2 divFree, 3 constDens
  readonly kendiIntegrasyonuVar = false; // Uses common integration pass

  private device: GPUDevice | null = null;
  private maxParticle: number = 0;

  // Pipeline infrastructure
  private pipelines: Map<string, GPUComputePipeline> = new Map();

  // Solver-specific buffers
  private solverBuffersRef: GPUSolverBuffers | null = null;
  private currentBindGroup: GPUBindGroup | null = null;

  // Solver parameters
  private params: DFSPHParametreler = {
    divFreeIter: 2,
    constDensIter: 3,
    densityError: 0.001
  };

  /**
   * Initialize the solver with GPU device and constraints
   */
  async basla(
    device: GPUDevice,
    maxParticle: number,
    ortakBindGroupLayout: GPUBindGroupLayout
  ): Promise<void> {
    this.device = device;
    this.maxParticle = maxParticle;

    // Create 5 compute pipelines with auto layout
    await this.createPipelines();
  }

  /**
   * Create all 5 DFSPH compute pipelines
   */
  private async createPipelines(): Promise<void> {
    if (!this.device) {
      throw new Error('Device not initialized');
    }

    const pipelineConfigs = [
      { name: 'computeDensityAlpha', shader: DFSPH_COMPUTE_DENSITY_ALPHA, label: 'DFSPH Compute Density+Alpha' },
      { name: 'nonPressureForces', shader: DFSPH_NON_PRESSURE_FORCES, label: 'DFSPH Non-Pressure Forces' },
      { name: 'predictVelocity', shader: DFSPH_PREDICT_VELOCITY, label: 'DFSPH Predict Velocity' },
      { name: 'divergenceSolve', shader: DFSPH_DIVERGENCE_SOLVE, label: 'DFSPH Divergence Solve' },
      { name: 'densitySolve', shader: DFSPH_DENSITY_SOLVE, label: 'DFSPH Density Solve' }
    ];

    for (const config of pipelineConfigs) {
      const shaderModule = this.device.createShaderModule({
        label: `${config.label} Shader`,
        code: config.shader
      });

      const pipeline = this.device.createComputePipeline({
        label: config.label,
        layout: 'auto',
        compute: {
          module: shaderModule,
          entryPoint: 'main'
        }
      });

      this.pipelines.set(config.name, pipeline);
    }
  }

  /**
   * Create solver-specific GPU buffers
   *
   * DFSPH requires:
   * - alpha: Density stiffness factor per particle
   * - densityAdv: Advanced density prediction
   */
  olusturBufferlar(device: GPUDevice, maxParticle: number): GPUSolverBuffers {
    const alphaBuffer = device.createBuffer({
      label: 'DFSPH Alpha Buffer',
      size: maxParticle * 4, // f32 per particle
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const densityAdvBuffer = device.createBuffer({
      label: 'DFSPH Density Advanced Buffer',
      size: maxParticle * 4, // f32 per particle
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST
    });

    const buffers = new Map<string, GPUBuffer>([
      ['alpha', alphaBuffer],
      ['densityAdv', densityAdvBuffer]
    ]);

    return {
      buffers,
      totalBytes: maxParticle * 8,
      dispose: () => {
        alphaBuffer.destroy();
        densityAdvBuffer.destroy();
      }
    };
  }

  /**
   * Get the bind group layout for solver buffers
   *
   * Returns layout from first pipeline (all pipelines share same @group(3) layout)
   */
  solverBindGroupLayout(): GPUBindGroupLayout {
    const pipeline = this.pipelines.get('computeDensityAlpha');
    if (!pipeline) {
      throw new Error('Pipelines not initialized');
    }
    return pipeline.getBindGroupLayout(3);
  }

  /**
   * Create bind group for solver buffers
   *
   * @group(3) bindings:
   * - @binding(0): alpha buffer (storage, read_write)
   * - @binding(1): densityAdv buffer (storage, read_write)
   */
  olusturBindGroup(device: GPUDevice, buffers: GPUSolverBuffers): GPUBindGroup {
    const layout = this.solverBindGroupLayout();

    const alphaBuffer = buffers.buffers.get('alpha');
    const densityAdvBuffer = buffers.buffers.get('densityAdv');

    if (!alphaBuffer || !densityAdvBuffer) {
      throw new Error('Required DFSPH buffers not found');
    }

    this.currentBindGroup = device.createBindGroup({
      label: 'DFSPH Solver Bind Group',
      layout,
      entries: [
        {
          binding: 0,
          resource: { buffer: alphaBuffer }
        },
        {
          binding: 1,
          resource: { buffer: densityAdvBuffer }
        }
      ]
    });

    this.solverBuffersRef = buffers;
    return this.currentBindGroup;
  }

  /**
   * Encode all DFSPH solver compute passes
   *
   * Execution order:
   * 1. Compute density and alpha factors
   * 2. Apply non-pressure forces (viscosity, surface tension)
   * 3. Predict velocity with current forces
   * 4. Divergence-free correction loop (default 2 iterations)
   * 5. Constant density correction loop (default 3 iterations)
   */
  encodeSolverPasslari(
    encoder: GPUCommandEncoder,
    ortakBindGroup: GPUBindGroup,
    particleBindGroup: GPUBindGroup,
    gridBindGroup: GPUBindGroup,
    solverBindGroup: GPUBindGroup,
    workgroupCount: number
  ): void {
    // Pass 1: Compute density and alpha factors
    this.encodePass(
      encoder,
      'computeDensityAlpha',
      'DFSPH Density+Alpha',
      ortakBindGroup,
      particleBindGroup,
      gridBindGroup,
      solverBindGroup,
      workgroupCount
    );

    // Pass 2: Non-pressure forces (viscosity, surface tension)
    this.encodePass(
      encoder,
      'nonPressureForces',
      'DFSPH NonPressure Forces',
      ortakBindGroup,
      particleBindGroup,
      gridBindGroup,
      solverBindGroup,
      workgroupCount
    );

    // Pass 3: Predict velocity
    this.encodePass(
      encoder,
      'predictVelocity',
      'DFSPH Predict Velocity',
      ortakBindGroup,
      particleBindGroup,
      gridBindGroup,
      solverBindGroup,
      workgroupCount
    );

    // Divergence-free correction loop
    for (let i = 0; i < this.params.divFreeIter; i++) {
      this.encodePass(
        encoder,
        'divergenceSolve',
        `DFSPH DivFree Iter #${i + 1}`,
        ortakBindGroup,
        particleBindGroup,
        gridBindGroup,
        solverBindGroup,
        workgroupCount
      );
    }

    // Constant density correction loop
    for (let i = 0; i < this.params.constDensIter; i++) {
      this.encodePass(
        encoder,
        'densitySolve',
        `DFSPH ConstDens Iter #${i + 1}`,
        ortakBindGroup,
        particleBindGroup,
        gridBindGroup,
        solverBindGroup,
        workgroupCount
      );
    }
  }

  /**
   * Encode a single compute pass with proper bind group setup
   */
  private encodePass(
    encoder: GPUCommandEncoder,
    pipelineName: string,
    label: string,
    ortakBindGroup: GPUBindGroup,
    particleBindGroup: GPUBindGroup,
    gridBindGroup: GPUBindGroup,
    solverBindGroup: GPUBindGroup,
    workgroupCount: number
  ): void {
    const pipeline = this.pipelines.get(pipelineName);
    if (!pipeline) {
      console.warn(`DFSPH pipeline '${pipelineName}' not found`);
      return;
    }

    const pass = encoder.beginComputePass({ label });
    pass.setPipeline(pipeline);

    // Bind groups:
    // @group(0): Uniform parameters (ortak)
    // @group(1): Particle data (positions, velocities, etc.)
    // @group(2): Grid data (spatial hashing)
    // @group(3): Solver-specific data (alpha, densityAdv)
    pass.setBindGroup(0, ortakBindGroup);
    pass.setBindGroup(1, particleBindGroup);
    pass.setBindGroup(2, gridBindGroup);
    pass.setBindGroup(3, solverBindGroup);

    pass.dispatchWorkgroups(workgroupCount);
    pass.end();
  }

  /**
   * Update solver parameters at runtime
   */
  guncelleParametreler(params: DFSPHParametreler): void {
    this.params = { ...this.params, ...params };

    // Recalculate total pass count
    const newPassCount = 3 + this.params.divFreeIter + this.params.constDensIter;
    (this as any).passSayisi = newPassCount;
  }

  /**
   * Cleanup GPU resources
   */
  temizle(): void {
    // Dispose pipelines
    this.pipelines.clear();

    // Dispose solver buffers if we created them
    if (this.solverBuffersRef) {
      this.solverBuffersRef.dispose();
      this.solverBuffersRef = null;
    }

    this.currentBindGroup = null;
    this.device = null;
  }

  /**
   * Get all shader source code for debugging/inspection
   */
  shaderKaynagi(): string {
    return [
      '// ===== DFSPH Solver Bindings =====',
      DFSPH_SOLVER_BINDINGS,
      '',
      '// ===== 1. Compute Density + Alpha =====',
      DFSPH_COMPUTE_DENSITY_ALPHA,
      '',
      '// ===== 2. Non-Pressure Forces =====',
      DFSPH_NON_PRESSURE_FORCES,
      '',
      '// ===== 3. Predict Velocity =====',
      DFSPH_PREDICT_VELOCITY,
      '',
      '// ===== 4. Divergence Solve =====',
      DFSPH_DIVERGENCE_SOLVE,
      '',
      '// ===== 5. Density Solve =====',
      DFSPH_DENSITY_SOLVE
    ].join('\n');
  }
}

// Export both as default and named export
export default FikoDFSPHSolver;
